import type { ReactElement, RefObject } from 'react';
import React, { cloneElement } from 'react';
import { useTheme } from 'styled-components';

import type { Color as ThemeColor } from '../../../style/types/theme';
import Spinner from '../../Spinner/Spinner';
import { Size } from '../constants';
import type { OriginalButtonProps, OriginalLinkProps } from '../types';
import { Variant } from './constants';
import type { ColorStyles } from './types';
import {
  Background,
  ChildrenWrapper,
  ContentWrapper,
  IconWrapper,
  SpinnerWrapper,
  StyledIcon,
  Wrapper,
} from './BaseButton.styled';

const getLoaderColor = (
  color: ThemeColor,
  variant: Variant,
  primaryColor?: ColorStyles,
  secondaryColor?: ColorStyles,
): string => {
  const variantLoaderColor = {
    [Variant.TEXT]: primaryColor?.default ?? color.content.secondary.default(),
    [Variant.BACKGROUND]: secondaryColor?.default ?? color.content.reversed.default(),
    [Variant.STROKE]: secondaryColor?.default ?? color.content.reversed.default(),
  };

  return variantLoaderColor[variant] || variantLoaderColor[Variant.BACKGROUND];
};

const isLink = (
  props: OriginalLinkProps | OriginalButtonProps,
): props is OriginalLinkProps => {
  return typeof (props as OriginalLinkProps).href === 'string';
};

type ClonedIconProps = (Partial<unknown> & React.Attributes) | undefined;

export interface CommonProps {
  /**
   * Icon to display in front of text
   */
  StartIcon?: ReactElement;
  /**
   * Icon to display at the end of text
   */
  EndIcon?: ReactElement;
  /**
   * Changes the text to a loader
   */
  loading?: boolean;
  /**
   * Changes the size. It can be one of the following options:
   * `Button.Size.SMALL`, `Button.Size.MEDIUM`, `Button.Size.BIG`
   */
  size?: Size;
  /**
   * Width of the component
   */
  width?: string;
  /**
   * Height of the component
   */
  height?: string;
  /**
   * Property used for testing purposes
   */
  dataCy?: string;
  /**
   * Primary colors of the button that affect the button differently depending on the variant.
   * Colors can be defined for the default, hover and disabled states, receiving an object with those keys
   */
  primaryColor: ColorStyles;
  /**
   * colors for the button icon. Colors can be defined for the default, hover and disabled states,
   * receiving an object with those keys
   */
  iconColor?: Partial<ColorStyles>;
  /**
   * Property used to create an ellipsis with the text
   */
  ellipsis?: boolean;
  /**
   * Property used to get the reference of the text. It can be used to know if the text is truncated
   */
  textRef?: RefObject<HTMLDivElement>;
}

interface BackgroundBaseButtonProps extends CommonProps {
  /**
   * Variant on which the button's style depends. Can be:
   * `Button.Variant.BACKGROUND`
   */
  // TODO: Move Variant.Stroke to own interface
  variant: Variant.BACKGROUND | Variant.STROKE;
  /**
   * Secondary colors of the button that affect the button differently depending on the variant.
   * Colors can be defined for the default, hover and disabled states, receiving an object with those keys
   */
  secondaryColor?: ColorStyles;
}

interface BorderBaseButtonProps extends CommonProps {
  variant: Variant.BORDER;
  secondaryColor?: ColorStyles;
}

interface TextBaseButtonProps extends CommonProps {
  variant: Variant.TEXT;
  secondaryColor?: undefined;
}

type PropsWithVariants =
  | BackgroundBaseButtonProps
  | BorderBaseButtonProps
  | TextBaseButtonProps;

export type InternalLinkProps = OriginalLinkProps & PropsWithVariants;
export type InternalButtonProps = OriginalButtonProps & PropsWithVariants;

export type BaseButtonProps = InternalLinkProps | InternalButtonProps;

export const BaseButton = ({
  StartIcon,
  EndIcon,
  children,
  loading = false,
  size = Size.MEDIUM,
  variant = Variant.BACKGROUND,
  width = 'fit-content',
  height = 'fit-content',
  dataCy,
  primaryColor,
  secondaryColor,
  iconColor,
  ellipsis = false,
  textRef,
  ...buttonProps
}: BaseButtonProps) => {
  const hasStartIcon = !!StartIcon;
  const hasEndIcon = !!EndIcon;
  const props = buttonProps as OriginalLinkProps | OriginalButtonProps;
  delete props.className;

  const { disabled } = props;
  const isDisabled = loading || !!disabled;
  const isButton = !isLink(props);
  const specificButtonProps = isButton ? { disabled: isDisabled } : null;
  const specificLinkProps = !isButton && !!disabled ? { href: undefined } : null;
  const { color } = useTheme();

  const ClonedIcon = (props: ClonedIconProps) =>
    StartIcon ? cloneElement(StartIcon, props) : null;

  const ClonedEndIcon = (props: ClonedIconProps) =>
    EndIcon ? cloneElement(EndIcon, props) : null;

  return (
    <Wrapper
      as={isButton ? 'button' : 'a'}
      $variant={variant}
      $loading={loading}
      $disabled={isDisabled}
      data-cy={dataCy}
      $width={width}
      $height={height}
      $primaryColor={primaryColor}
      $secondaryColor={secondaryColor}
      $iconColor={iconColor}
      $ellipsis={ellipsis}
      {...props} // eslint-disable-line react/jsx-props-no-spreading
      {...specificButtonProps} // eslint-disable-line react/jsx-props-no-spreading
      {...specificLinkProps} // eslint-disable-line react/jsx-props-no-spreading
    >
      <Background
        data-testid="background"
        $variant={variant}
        $disabled={!!disabled}
        $primaryColor={primaryColor}
      />
      {loading && (
        <SpinnerWrapper>
          <Spinner color={getLoaderColor(color, variant, primaryColor, secondaryColor)} />
        </SpinnerWrapper>
      )}
      <ContentWrapper
        data-testid="content-wrapper"
        $variant={variant}
        $disabled={isDisabled}
        $loading={loading}
        $size={size}
        $hasStartIcon={hasStartIcon}
        $hasEndIcon={hasEndIcon}
        $primaryColor={primaryColor}
        $secondaryColor={secondaryColor}
        $ellipsis={ellipsis}
      >
        {hasStartIcon && !!ClonedIcon && (
          <IconWrapper $size={size}>
            <StyledIcon $defaultIconColor={iconColor?.default} as={ClonedIcon} />
          </IconWrapper>
        )}

        <ChildrenWrapper $ellipsis={ellipsis} ref={textRef}>
          {children}
        </ChildrenWrapper>

        {hasEndIcon && !!ClonedEndIcon && (
          <IconWrapper $size={size} $isEndIcon>
            <StyledIcon $defaultIconColor={iconColor?.default} as={ClonedEndIcon} />
          </IconWrapper>
        )}
      </ContentWrapper>
    </Wrapper>
  );
};

BaseButton.Variant = Variant;
BaseButton.Size = Size;
